/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

export const LogLevelSchema = z.enum(['debug', 'info', 'warn', 'error']);
export type LogLevel = z.infer<typeof LogLevelSchema>;

/**
 * What an entry is: plain output (`log`), an error the app reported about the camera (`ptp`), its page (`ui`) or
 * itself (`app`), the app dying (`crash`), the device's environment (`env`), something the app did that a check looks
 * for (`event`), or the app's check of itself at start (`diagnosis`).
 */
export const LogKindSchema = z.enum(['log', 'ptp', 'ui', 'app', 'crash', 'env', 'event', 'diagnosis']);
export type LogKind = z.infer<typeof LogKindSchema>;

/** The kinds of error that can start a fix; a plain log line at error level is information, not a fault. */
export const FAULT_KINDS: readonly LogKind[] = ['ptp', 'ui', 'app', 'crash'];

/** One entry as a device sends it. */
export const DeviceLogEntrySchema = z.object({
  ts: z.string().max(40),
  level: LogLevelSchema,
  source: z.enum(['app', 'agent']),
  kind: LogKindSchema,
  code: z.string().max(10).optional(),
  operation: z.string().max(200).optional(),
  message: z.string().max(4000),
  data: z.record(z.string(), z.unknown()).optional(),
});
export type DeviceLogEntry = z.infer<typeof DeviceLogEntrySchema>;

/** What the IoT rule forwards from `jit/{device_id}/logs`: the batch, plus the device id the topic names. */
export const LogBatchEventSchema = z.object({
  device_id: z.string().uuid(),
  deployment_id: z.string().max(100).optional(),
  version: z.number().int().nonnegative(),
  entries: z.array(DeviceLogEntrySchema).max(100),
  dropped: z.number().int().nonnegative().optional(),
  received_at: z.number().int().positive(),
});
export type LogBatchEvent = z.infer<typeof LogBatchEventSchema>;

/** An entry as the API serves it. */
export const LogEntrySchema = z.object({
  project_id: z.string().uuid(),
  version: z.number().int().nonnegative(),
  ts: z.string().datetime(),
  level: LogLevelSchema,
  source: z.enum(['app', 'agent']),
  kind: LogKindSchema,
  code: z.string().nullable().default(null),
  operation: z.string().nullable().default(null),
  message: z.string(),
  data: z.record(z.string(), z.unknown()).nullable().default(null),
});
export type LogEntry = z.infer<typeof LogEntrySchema>;

export const ListLogsResponseSchema = z.object({
  entries: z.array(LogEntrySchema),
  /** Pass it back to get what came after these entries. */
  cursor: z.string().nullable(),
});
export type ListLogsResponse = z.infer<typeof ListLogsResponseSchema>;
